<template>
  <div id="regularInfo" class="product-compare-note">
    <h3>定期コースについて</h3>
    <p>
      <template v-for="(line, index) in lines" :key="index">
        {{ line }}<br v-if="index < lines.length - 1" />
      </template>
    </p>
  </div>

  <div v-if="detailHref" class="product-compare-note-btn-wrap">
    <PartsChromeLink :to="detailHref" class="product-compare-note-detail-btn">
      <p>定期コースの詳細はこちら</p>
      <span class="product-compare-note-detail-btn-arrow"></span>
    </PartsChromeLink>
  </div>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `product-compare-note`（`id="regularInfo"`・定期コースについて）。
 * 支給は 20/38 に出るが、**実装は21商品**（`NST` は定期を持たないため除く。決定#48-②。
 * 19商品 ＋ 3袋定期の `L61` `L62`・#1398）。
 *
 * **支給20本の当該ブロックは md5 完全一致＝1パターン。** 一方 本番は19商品で10種の文面がある。
 * 可変の軸は3つだけ（継続回数 ／ 配送間隔 ／ 単位）なので、**共通コンポーネント1本＋props** で描く。
 * 商品別 `.vue` を19本復活させない（slot を使わない）のは決定#47 のとおり。
 *
 * **ビジネスルールは本番が正**（決定#48）。支給は全商品「3回以上」で書かれているが、
 * `510` `512` `513` `516` は「2回以上」。配送間隔も商品ごとに違う。
 *
 * **配送間隔の出どころは API。** `quantityThresholdCodes` を持つ7商品
 * （`210` `501` `520` `530` `592` `810` `710`）は `availableCycles` を数量帯ごとに見る必要があり、
 * それ以外の定期商品は `regularCycle` を見る（決定#47 の訂正）。ここでは解決済みの配列を受け取る。
 *
 * **`unitNote` は既定で出さない。** 本番の `210` `520` `530` `592` `710` `810` には
 * 「※「2ヶ月毎」のお届けは、2袋（2箱）からお選びいただけます。」があるが支給では消えている。
 * どちらが正か決められないため人間レビュー待ち（§8-31②）。props で足せる形にしておく。
 *
 * 「定期コースの詳細はこちら」の飛び先も支給は `href="#"` で未確定（§8-31⑤。`/regular` と推定）。
 * 未定のうちは `#` が渡るので `NuxtLink` ではなく `PartsChromeLink` を通す。
 * → CONVERSION-GUIDE.md 決定#47・#48 ／ §3.3.6
 */
type Props = {
  /** 継続回数。標準3、`510` `512` `513` `516` は2 */
  minCount: number;
  /**
   * 変更・お休み・解約ができるようになる回数。**既定は `minCount + 1`**。
   *
   * **現在これを渡している商品は無い。** 3袋定期（`L61` `L62`）の 2026-09-03 の回答が
   * 「3回目以降」で既存19商品と規則が違ったため受け取れるようにしたが、
   * 2026-09-04 の再回答で誤記と判明し、全21商品が既定式に揃った（PR #1400 のレビュー4）。
   * 商品別に分かれたときにここで受け取れる。
   */
  changeableFrom?: number;
  /** 配送間隔。`[1]` / `[2]` / `[1, 2]` / `[3]`（3袋定期・#1398） */
  cycles: number[];
  /** 「袋」「箱」など。`unitNote` を出すときに使う */
  unit?: string;
  /** 「2ヶ月毎は2袋から」の注記を出すか（人間レビュー待ち・§8-31②） */
  unitNote?: boolean;
  /** 「定期コースの詳細はこちら」の飛び先。無ければボタンごと出さない */
  detailHref?: string;
};

const props = withDefaults(defineProps<Props>(), {
  unit: '袋',
  unitNote: false,
  detailHref: '',
});

/** 支給の文面から、商品で変わらない部分をそのまま持つ */
const BENEFITS: string[] = [
  '■定期コースの5大特典',
  '【特典1：お得な割引価格でお届け】定期コースだけの特別価格で購入できます。',
  '【特典2：毎回、送料・代引手数料無料】送料・代引き手数料はすべて当社が負担します。',
  '【特典3：お届け日や数量も変更可能】お届け日や個数の変更も自由に指定できます。',
  '【特典4：電話や問い合わせフォームで変更OK】変更手続きはマイページ・お電話で変更可能です。',
  '【特典5：お休みもOK】一時休止もできるので安心です♪',
];

/** 変更・お休み・解約ができるようになる回数。渡されなければ `minCount + 1`（既存19商品の規則） */
const changeableFrom = computed<number>(() => props.changeableFrom ?? props.minCount + 1);

/**
 * 配送間隔の文面。**`cycles` から規則で組む**（1ヶ月 = およそ30日）。
 * 1・2 を決め打ちで分岐すると、3ヵ月ごとが「およそ30日間隔」と誤表示になり、
 * `[1, 3]` `[1, 2, 3]` では3が黙って消える。
 * 既存19商品の実測（併記13／60日のみ4／30日のみ2）はこの式で再現できる。
 */
const cycleText = computed<string>(() => {
  const cycles = [...props.cycles].sort((a, b) => a - b);
  if (cycles.length === 0) return '';
  if (cycles.length === 1) return `およそ${cycles[0] * 30}日間隔`;

  return cycles.map((cycle) => `「${cycle}ヶ月毎」の場合はおよそ${cycle * 30}日間隔`).join('、');
});

const lines = computed<string[]>(() => [
  // 配送間隔が取れないときは行ごと落とす（既定値を書くと間隔を偽ることになる）
  ...(cycleText.value
    ? [
        `●お得な定期コースは、初回お届け日を基準に${cycleText.value}で自動的にお届けするコースです。`,
      ]
    : []),
  ...(props.unitNote && props.cycles.includes(2)
    ? [`※「2ヶ月毎」のお届けは、2${props.unit}からお選びいただけます。`]
    : []),
  `●実感いただくために、${props.minCount}回以上のご継続をお約束いただいております。`,
  `●${props.minCount}回目をお受け取り後、自動的にお届け終了とはなりません。`,
  `●${changeableFrom.value}回目以降からは、いつでも日程変更、お休み、再開、解約などが可能です。`,
  '※お届け日の変更、またはお休み・ご解約は、お届け予定日の10日前までにお電話またはお問い合わせフォームからお手続きください。',
  '●ご解約の連絡をいただくまでは、継続してお届けいたします。',
  '',
  ...BENEFITS,
]);
</script>
